import { useAppDispatch, useAppSelector } from "../../app/hooks";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import {
  generateLoremIpsumAsync,
  generationCountOnChanged,
  GenerationType,
  generationTypeOnChanged,
  loremIpsumSnippetCopied,
  selectState,
} from "./LoremIpsumGeneratorSlice";
import { SideMenuPageContainer } from "../../components/side_menu_page_container/SideMenuPageContainer";
import {
  Box,
  Divider,
  Grid,
  IconButton,
  List,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { CustomButton } from "../../components/custom_button/CustomButton";
import TextSnippetIcon from "@mui/icons-material/TextSnippetOutlined";
import { TaskAlt } from "@mui/icons-material";
import { Light as SyntaxHighlighter } from "react-syntax-highlighter";
import js from "react-syntax-highlighter/dist/esm/languages/hljs/xml";
import docco from "react-syntax-highlighter/dist/esm/styles/hljs/androidstudio";
import CopyIcon from "@mui/icons-material/ContentCopy";
import { HomeCard } from "../../components/home_card/HomeCard";
import QrCodeGeneratorImage from "../../assets/QrCodeGenerator.webp";
import PasswordGeneratorImage from "../../assets/PasswordGenerator.webp";
import { useTranslation } from "react-i18next";
import AdSense from "../../components/adsense/AdSense";
import { ADSENSE_SLOTS } from "../../config/adsense";

SyntaxHighlighter.registerLanguage("xml", js);

export function LoremIpsumGenerator() {
  const dispatch = useAppDispatch();
  const state = useAppSelector(selectState);
  const { t } = useTranslation();

  return (
    <SideMenuPageContainer
      pageTitle={t("loremIpsumGenerator.title")}
      pageSeoTitle={t("loremIpsumGenerator.seoTitle")}
      pageDescription={t("loremIpsumGenerator.description")}
    >
      <Grid2
        container
        spacing={2}
        sx={{ mt: 2 }}
        columns={{ xs: 6, sm: 6, md: 12 }}
      >
        <Grid2 xs={12}>
          <Typography>{t("loremIpsumGenerator.generate")}</Typography>
        </Grid2>
        <Grid2 xs={12}>
          <TextField
            variant="outlined"
            color="info"
            value={state.generationCount}
            onChange={(e) => {
              dispatch(generationCountOnChanged(Number(e.target.value)));
            }}
          />
        </Grid2>
        <Grid2 xs={12}>
          <ToggleButtonGroup
            color="secondary"
            value={state.generationType}
            exclusive
            onChange={(
              event: React.MouseEvent<HTMLElement>,
              newValue: string | null
            ) => {
              dispatch(
                generationTypeOnChanged(Number(newValue) as GenerationType)
              );
            }}
            aria-label="text alignment"
          >
            <ToggleButton
              value={GenerationType.words}
              aria-label="left aligned"
            >
              <Typography>{t("loremIpsumGenerator.options.words")}</Typography>
            </ToggleButton>
            <ToggleButton
              value={GenerationType.sentences}
              aria-label="left aligned"
            >
              <Typography>
                {t("loremIpsumGenerator.options.sentences")}
              </Typography>
            </ToggleButton>
            <ToggleButton
              value={GenerationType.paragraphs}
              aria-label="centered"
            >
              <Typography>
                {t("loremIpsumGenerator.options.paragraphs")}
              </Typography>
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid2>
        <Grid2 xs={12} sx={{ mb: 2 }}>
          <CustomButton
            icon={<TextSnippetIcon sx={{ color: "#FFD9A8" }} />}
            label={t("loremIpsumGenerator.generate")}
            width={180}
            onClick={() => {
              dispatch(generateLoremIpsumAsync());
            }}
          />
        </Grid2>
        <Grid2 xs={12}>
          <Stack direction="column" justifyContent="center" alignItems="center">
            <Box sx={{ width: { xs: 300, sm: 350, md: 600, lg: 700 } }}>
              <Box
                sx={{
                  mb: -1.8,
                  bgcolor: "#282B2E",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="helptext" sx={{ ml: 1, pt: 1, pb: 1 }}>
                  {t("loremIpsumGenerator.outputHeading")}
                </Typography>
                <IconButton
                  aria-label="copy"
                  sx={{ color: "#A5D9EE" }}
                  onClick={() => {
                    navigator.clipboard.writeText(state.generatedLoremIpsum);
                    dispatch(loremIpsumSnippetCopied(true));
                  }}
                >
                  {state.isLoremIpsumSnippetCopied ? (
                    <TaskAlt sx={{ color: "#97e286" }} />
                  ) : (
                    <CopyIcon />
                  )}
                </IconButton>
              </Box>
              <SyntaxHighlighter language="xml" style={docco}>
                {state.generatedLoremIpsum}
              </SyntaxHighlighter>
            </Box>
          </Stack>
        </Grid2>
        <Grid2 xs={12} sx={{ mt: 3, mb: 3 }}>
          <Divider sx={{ backgroundColor: "#7895a1" }}></Divider>
        </Grid2>
        <Grid2 xs={12}>
          <AdSense
                adSlot={ADSENSE_SLOTS.IN_ARTICLE}
                adFormat="fluid"
                adLayoutKey="-fb+5w+4e-db+86"
                sx={{ mb: 3 }}
              />
          <Typography variant="h3" sx={{ mb: 2 }}>
            {t("loremIpsumGenerator.content.whatIsHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t("loremIpsumGenerator.content.whatIsDescription")}
          </Typography>
          <Typography variant="h3" sx={{ mb: 2 }}>
            {t("loremIpsumGenerator.content.whyHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t("loremIpsumGenerator.content.whyDescriptionPart1")}
            <ul>
              <li>{t("loremIpsumGenerator.content.whyList.websiteMockups")}</li>
              <li>
                {t("loremIpsumGenerator.content.whyList.printDesignPreviews")}
              </li>
              <li>
                {t(
                  "loremIpsumGenerator.content.whyList.desktopPublishingSoftware"
                )}
              </li>
              <li>{t("loremIpsumGenerator.content.whyList.uiuxPrototypes")}</li>
            </ul>
            {t("loremIpsumGenerator.content.whyDescriptionPart2")}
          </Typography>
          <Typography variant="h3" sx={{ mb: 2 }}>
            {t("loremIpsumGenerator.content.whereFromHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t("loremIpsumGenerator.content.whereFromDescription")}
          </Typography>
          <Typography variant="h3" sx={{ mb: 2 }}>
            {t("loremIpsumGenerator.content.howToHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t("loremIpsumGenerator.content.howToDescriptionPart1")}
            <ul>
              <li>
                {t("loremIpsumGenerator.content.howToList.avoidsRepetitive")}
              </li>
              <li>
                {t("loremIpsumGenerator.content.howToList.providesLengths")}
              </li>
              <li>{t("loremIpsumGenerator.content.howToList.cleanSafe")}</li>
              <li>
                {t("loremIpsumGenerator.content.howToList.classicalLatin")}
              </li>
            </ul>
          </Typography>
          <Typography variant="h3" sx={{ mb: 2 }}>
            {t("loremIpsumGenerator.content.referencesHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            <ul>
              <li>{t("loremIpsumGenerator.content.referencesList1")}</li>
              <li>{t("loremIpsumGenerator.content.referencesList2")}</li>
            </ul>
            {t("loremIpsumGenerator.content.referencesFooter")}
          </Typography>
        </Grid2>
        <Grid2 xs={12}>
          <Typography variant="h3" sx={{ mt: 3, mb: 1 }}>
            {t("loremIpsumGenerator.related")}
          </Typography>
        </Grid2>
        <Grid2 xs={6}>
          <HomeCard
            languageResource="passwordGenerator"
            link="/PasswordGenerator"
            smallCard={true}
            image={PasswordGeneratorImage}
          />
        </Grid2>
        <Grid2 xs={6}>
          <HomeCard
            languageResource="qrCodeGenerator"
            link="/QrCodeGenerator"
            smallCard={true}
            image={QrCodeGeneratorImage}
          />
        </Grid2>
      </Grid2>
    </SideMenuPageContainer>
  );
}
